import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import FormContainer from "../../../components/FormContainer";
import KategoriBrg from '../../../models/KategoriBrg';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { PageContainerActionParamType } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import { validate as uuidValidate } from "uuid";

const groupName: MenuGroupName = "Stok";
const subgroupName: string = "Master";
const code: MenuCode = "STOCK-KATEGORI-BRG";
const title: MenuTitle = "Kategori Barang";
const endpoint = "kategoribrg";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<KategoriBrg>({ kode: '', nama: '', aktif: true });
    const navigate = useNavigate();
    
    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => setFormData({...response.data?.data}))
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: KategoriBrg | undefined) => {
        if (!data?.nama)
            return false;

        if (data?.ucode && !data.kode)
            return false;

        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<KategoriBrg> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const apiCallerObj = param.data.ucode ? apiCaller.put(`${endpoint}`, JSON.stringify(param.data)) : apiCaller.post(`${endpoint}`, JSON.stringify(param.data));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<KategoriBrg>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
					isViewMode={isViewMode}
                    fields={[
                        {
                            name: "kode",
                            title: "Kode",
                            inputType: "text",
                            dataType: (typeof formData.kode),
                            isReadonly: false,
                            isRequired: formData?.ucode ? true : false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<KategoriBrg>) => onChanged(valueObj),
                        },
                        {
                            name: "nama",
                            title: "Nama",
                            inputType: "text",
                            dataType: (typeof formData.nama),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<KategoriBrg>) => onChanged(valueObj),
                        },
                        {
                            name: "keterangan",
                            title: "Keterangan",
                            inputType: "textarea",
                            dataType: (typeof formData.keterangan),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<KategoriBrg>) => onChanged(valueObj),
                            rows: 3,
                        }
                    ]}
                    onValidate={onValidate}
                    onClose={(pathObject) => onClose(pathObject)}
                    onSave={(data) => onSave(data)}
                />
            </FormContainer>
        </>
    );
};

export default Form;
